<script lang="ts">
    import { onMount, tick } from 'svelte';
    import { getPathParams } from 'svelte-mini-router';
    import { translate } from '@/utils/format';
    import { goTo } from '@/utils/router';
    import { getSsoSession, setSsoSession, savePartnerProfile } from '@/services/sso';
    import { loadOptions, GOAL_OPTIONS, TRAININGS } from '../../../../contracts';

    type Question = {
        id: string;
        text: string;
        options: Array<{ value: string; label: string }>;
    };

    let partnerId = $state('');
    let currentStep = $state(0);
    let answers = $state<Record<string, string>>({});
    let animating = $state(false);
    let goalOptions = $state<Array<{ value: string; text: string }>>([]);
    let trainingOptions = $state<Array<{ id: string; name: string }>>([]);

    const questions = $derived<Question[]>([
        {
            id: 'goal',
            text: translate('NJO_Q_GOAL', '¿Cuál es tu objetivo principal?'),
            options: goalOptions.map((o) => ({ value: String(o.value ?? ''), label: o.text ?? '' })),
        },
        {
            id: 'sport',
            text: translate('NJO_Q_SPORT', '¿Qué deporte practicas?'),
            options: trainingOptions.map((t) => ({ value: t.id, label: t.name ?? '' })),
        },
        {
            id: 'freq',
            text: translate('NJO_Q_FREQ', '¿Cuántos días entrenas a la semana?'),
            options: [
                { value: 'low', label: translate('NJO_OPT_FREQ_LOW', '1-2 días por semana') },
                { value: 'med', label: translate('NJO_OPT_FREQ_MED', '3-4 días por semana') },
                { value: 'high', label: translate('NJO_OPT_FREQ_HIGH', '5 o más días') },
            ],
        },
        {
            id: 'foodRel',
            text: translate('NJO_Q_FOOD_REL', '¿Cómo es tu relación con la alimentación?'),
            options: [
                { value: 'hard', label: translate('NJO_OPT_FOOD_HARD', 'Es un reto para mí') },
                { value: 'med', label: translate('NJO_OPT_FOOD_MED', 'Me las apaño') },
                { value: 'easy', label: translate('NJO_OPT_FOOD_EASY', 'Lo tengo controlado') },
            ],
        },
        {
            id: 'priority',
            text: translate('NJO_Q_PRIORITY', '¿Qué quieres mejorar primero?'),
            options: [
                { value: 'energy', label: translate('NJO_OPT_PRIO_ENERGY', 'Más energía') },
                { value: 'recovery', label: translate('NJO_OPT_PRIO_RECOVERY', 'Mejor recuperación') },
                { value: 'body', label: translate('NJO_OPT_PRIO_BODY', 'Composición corporal') },
                { value: 'lose', label: translate('NJO_OPT_PRIO_LOSE', 'Perder grasa') },
                { value: 'muscle', label: translate('NJO_OPT_PRIO_MUSCLE', 'Ganar músculo') },
            ],
        },
    ]);

    const totalSteps = $derived(questions.length);
    const currentQuestion = $derived(questions[currentStep]);
    const progress = $derived(((currentStep + 1) / totalSteps) * 100);

    async function selectOption(value: string) {
        answers = { ...answers, [currentQuestion.id]: value };

        // Non-blocking incremental save
        const session = getSsoSession();
        if (session?.webToken) {
            savePartnerProfile(session.webToken, {
                status: 'onboarding_completed',
                onboardingAnswers: answers,
            }).catch(() => {/* non-blocking */});
        }

        if (currentStep < totalSteps - 1) {
            animating = true;
            await tick();
            setTimeout(() => {
                currentStep++;
                animating = false;
            }, 200);
        } else {
            // All done: update session answers then go to result
            if (session) {
                setSsoSession({ ...session, answers });
            }
            goTo(`/${partnerId}/result`);
        }
    }

    function goBack() {
        if (currentStep > 0) currentStep--;
    }

    onMount(async () => {
        const params = getPathParams();
        partnerId = String(params?.['partnerId'] || '').trim();

        if (!partnerId) {
            goTo('/');
            return;
        }

        await loadOptions();
        GOAL_OPTIONS.subscribe((v) => { goalOptions = v as any; });
        TRAININGS.subscribe((v) => { trainingOptions = v; });
    });
</script>

<section class="ssoOnb">
    <!-- Progress bar -->
    <div class="ssoOnb__progressBar">
        <div class="ssoOnb__progressFill" style="width: {progress}%"></div>
    </div>

    <!-- Header -->
    <header class="ssoOnb__header">
        {#if currentStep > 0}
            <button class="ssoOnb__back" type="button" onclick={goBack} aria-label="Atrás">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <path d="M19 12H5M12 5l-7 7 7 7" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
        {:else}
            <div></div>
        {/if}
        <span class="ssoOnb__stepLabel">
            {translate('SSO_STEP_OF', '{current} de {total}', {
                current: String(currentStep + 1),
                total: String(totalSteps),
            })}
        </span>
    </header>

    <!-- Question area -->
    <div class="ssoOnb__body" class:ssoOnb__body--animating={animating}>
        {#if currentQuestion}
            <h2 class="ssoOnb__question">{currentQuestion.text}</h2>
            <div class="ssoOnb__options">
                {#each currentQuestion.options as opt (opt.value)}
                    <button
                        class="ssoOnb__option"
                        class:ssoOnb__option--selected={answers[currentQuestion.id] === opt.value}
                        type="button"
                        onclick={() => selectOption(opt.value)}
                    >
                        {opt.label}
                    </button>
                {/each}
            </div>
        {/if}
    </div>

    <!-- Dots indicator -->
    <div class="ssoOnb__dots">
        {#each questions as _q, i (i)}
            <div class="ssoOnb__dot" class:ssoOnb__dot--active={i === currentStep} class:ssoOnb__dot--done={i < currentStep}></div>
        {/each}
    </div>

    <p class="ssoOnb__poweredBy">{translate('SSO_POWERED_BY', 'powered by Caloo')}</p>
</section>

<style lang="scss">
    .ssoOnb {
        min-height: 100vh;
        background: var(--backgroundMain);
        color: var(--textColor);
        display: grid;
        grid-template-rows: 4px auto 1fr auto auto;
        font-family: 'Montserrat', sans-serif;
        overflow: hidden;
    }

    .ssoOnb__progressBar {
        width: 100%;
        height: 4px;
        background: var(--border);
    }

    .ssoOnb__progressFill {
        height: 100%;
        background: var(--partner-primary, var(--primary));
        transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .ssoOnb__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px 20px 8px;
    }

    .ssoOnb__back {
        width: 36px;
        height: 36px;
        border: none;
        border-radius: 10px;
        background: var(--white);
        color: var(--textColor);
        display: grid;
        place-items: center;
        cursor: pointer;
        border: 1px solid var(--border);
    }

    .ssoOnb__stepLabel {
        font-size: 12px;
        font-weight: 700;
        color: var(--subtleText);
        letter-spacing: 0.04em;
    }

    .ssoOnb__body {
        display: grid;
        align-content: start;
        gap: 20px;
        padding: 32px 20px 20px;
        transition: opacity 0.2s ease;
    }

    .ssoOnb__body--animating {
        opacity: 0;
        pointer-events: none;
    }

    .ssoOnb__question {
        margin: 0;
        font-size: 22px;
        font-weight: 900;
        color: var(--black);
        letter-spacing: -0.02em;
        line-height: 1.25;
    }

    .ssoOnb__options {
        display: grid;
        gap: 10px;
        margin-top: 4px;
    }

    .ssoOnb__option {
        width: 100%;
        padding: 16px 20px;
        border-radius: 14px;
        border: 1.5px solid var(--border);
        background: var(--white);
        color: var(--textColor);
        font-size: 15px;
        font-weight: 700;
        font-family: inherit;
        text-align: left;
        cursor: pointer;
        transition: border-color 0.15s, background 0.15s, transform 0.1s;
    }

    .ssoOnb__option:hover {
        border-color: var(--partner-primary, var(--primary));
        background: rgba(76, 227, 157, 0.05);
    }

    .ssoOnb__option--selected {
        border-color: var(--partner-primary, var(--primary));
        background: rgba(76, 227, 157, 0.08);
        color: var(--black);
    }

    .ssoOnb__option:active {
        transform: scale(0.98);
    }

    .ssoOnb__dots {
        display: flex;
        gap: 6px;
        justify-content: center;
        align-items: center;
        padding: 16px 20px;
    }

    .ssoOnb__dot {
        width: 8px;
        height: 8px;
        border-radius: 999px;
        background: var(--border);
        transition: background 0.2s, width 0.2s;
    }

    .ssoOnb__dot--active {
        background: var(--partner-primary, var(--primary));
        width: 20px;
    }

    .ssoOnb__dot--done {
        background: var(--partner-primary, var(--primary));
        opacity: 0.4;
    }

    .ssoOnb__poweredBy {
        font-size: 11px;
        color: var(--subtleText);
        opacity: 0.5;
        text-align: center;
        padding: 0 0 24px;
        letter-spacing: 0.04em;
    }
</style>
